import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { theme, toRem } from 'design';
import { selectCurrentEmployee, setCurrentEmployeeRequest } from 'store/App';
import { selectCurrentEmployeeManagedSites, selectIsPermitted } from 'store/Employers';
import { EmployeeRole, IEmployerSite } from 'types';
import { getSiteSwitcherInfo } from 'utils';
import SiteSelector from './SiteSelector';
import { SiteSelectorAppearance } from './types';

interface IConnectedSiteSelector {
  appearance?: SiteSelectorAppearance;
  allSitesEnabled?: boolean;
  onSiteSelect?: (site: IEmployerSite) => void;
}

const SiteSelectorContainer = styled.div`
  position: relative;
  display: flex;
  flex-grow: 1;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  margin-bottom: ${toRem(22)};

  @media (min-width: ${theme.breakpoint.small}px) {
    align-items: flex-start;
    width: auto;
    margin-bottom: 0;
  }
`;

const ConnectedSiteSelector = ({ appearance = SiteSelectorAppearance.DEFAULT, allSitesEnabled, onSiteSelect }: IConnectedSiteSelector) => {
  const dispatch = useDispatch();
  const isMounted = useRef(false);
  const currentEmployee = useSelector(selectCurrentEmployee());
  const currentEmployeeSites = useSelector(selectCurrentEmployeeManagedSites);

  const handleSiteSelect = (selected: IEmployerSite) => {
    if (selected) {
      const siteId = selected.id;
      dispatch(setCurrentEmployeeRequest({ ...currentEmployee, siteId }));
    } else {
      dispatch(setCurrentEmployeeRequest({ ...currentEmployee, siteId: undefined }));
    }
  };

  const allSitesRollupPermitted = useSelector(selectIsPermitted([EmployeeRole.ADMIN]));
  const siteSwitcherInfo = getSiteSwitcherInfo(currentEmployeeSites || [], currentEmployee?.siteId);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;

      const site = siteSwitcherInfo.currentSite || siteSwitcherInfo.options[0];
      if (site) {
        dispatch(setCurrentEmployeeRequest({ ...currentEmployee, siteId: site.id }));
      }
    }
  }, [dispatch, siteSwitcherInfo]);

  return (
    <SiteSelectorContainer>
      {siteSwitcherInfo.options && (
        <SiteSelector options={siteSwitcherInfo.options} currentSite={siteSwitcherInfo.currentSite} onChange={onSiteSelect || handleSiteSelect} appearance={appearance} allSitesEnabled={allSitesEnabled && allSitesRollupPermitted} />
      )}
    </SiteSelectorContainer>
  );
};

export default ConnectedSiteSelector;
